import React, { useEffect, useState, useRef } from "react";

// Components //
import Slider from "react-slick";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import Button from "../../atoms/button/button";

// Styles //
import "./main-banner.scss";
import "slick-carousel/slick/slick.scss";
import "slick-carousel/slick/slick-theme.scss";

import { ReferenciaContenido } from "../../../interfaces/ReferenciaContenido";

// Utils //
import { splitButtonText, isMobileHelper } from "../../../utils/helpers";
import useElementSize from "../../../hooks/useElementSize";

const MainBanner = (props: ReferenciaContenido) => {
    const refPlantilla = props.referenciaPlantillaContenido;
    const [isMobile, setIsMobile] = useState(isMobileHelper());
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderRef = useRef<Slider | null>(null);
    const headerSize = useElementSize(".m-header");
    const bannerRef = useRef<HTMLDivElement | null>(null);
    const dotContainerRef = useRef<HTMLDivElement | null>(null);
    const hasNextSlide = () => currentSlide < refPlantilla?.length - 1;
    const hasPrevSlide = () => currentSlide > 0;

    const CustomPrevArrow = (props: any) => {
        const { onClick } = props;
        const canGoPrev = hasPrevSlide();
        
        return (
            <button
                onClick={onClick}
                className={`slick-arrow slick-prev ${!canGoPrev ? 'disabled' : ''}`}
                style={{ 
                    backgroundColor: canGoPrev ? '#E6007E' : '#FCE3ED',
                    cursor: canGoPrev ? 'pointer' : 'default'
                }}
                type="button"
                aria-label="Anterior"
            />
        );
    };

    const CustomNextArrow = (props: any) => {
        const { onClick } = props;
        const canGoNext = hasNextSlide();

        return (
            <button
                onClick={onClick}
                className={`slick-arrow slick-next ${!canGoNext ? 'disabled' : ''}`}
                style={{ 
                    backgroundColor: canGoNext ? '#E6007E' : '#fce3ed',
                    cursor: canGoNext ? 'pointer' : 'default'
                }}
                type="button"
                aria-label="Siguiente"
            />
        );
    };

    const HomeSliderSettings  = {
        dots: true,
        infinite: false,
        speed: 300,
        slidesToShow: 3,
        slidesToScroll: 1,
        waitForAnimate: false,
        autoplay: false,
        fade: true,
        autoplaySpeed: 10000,
        prevArrow: <CustomPrevArrow />,
        nextArrow: <CustomNextArrow />,
        dotsClass: "slick-dots native-slick-dots",
        beforeChange: (_: number, nextSlide: number) => {
            setCurrentSlide(nextSlide);
        },
        appendDots(dots) {
            return (
               <ul>
                  {dots}
               </ul>
            )
        },
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(isMobileHelper());
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    

    useEffect(() => {
      if (bannerRef.current && dotContainerRef.current) {
        // Obtener los dots generados por react-slick
        const dots = bannerRef.current.querySelector('.native-slick-dots') as HTMLElement;
        
        if (dots) {
           const dotsCopy = dots.cloneNode(true) as HTMLElement;
           dotsCopy.classList.remove('native-slick-dots');
           dotsCopy.classList.add('banner-dots');

           const dotItems = dotsCopy.querySelectorAll('li');
           dotItems.forEach((item, index) => {
               item.addEventListener('click', () => {
                   sliderRef.current?.slickGoTo(index);
               })
           });

          // Mover los dots al contenedor personalizado
          if (dotContainerRef?.current) {
            dotContainerRef.current.innerHTML = '';
            dotContainerRef?.current?.appendChild(dotsCopy);
          }
        }
      }
    }, [currentSlide]);

    return (
        <div className="o-banner" style={{ marginTop: `${headerSize.height}px` }} ref={bannerRef}>
            <div className="o-banner-container">
                <div className="container">
                    <div className="banner-text">
                        <h1>{refPlantilla[0].tituloBanner}</h1>
                        {refPlantilla[0].imagenPerfil && (
                            <div className="banner-text__logo">
                                <img
                                    src={refPlantilla[0].imagenPerfil.file.url}
                                    alt={refPlantilla[0].imagenPerfil.file.fileName}
                                />
                            </div>
                        )}
                        <p className="banner-text__subtitle">
                            {refPlantilla[0].descripcion?.raw &&
                                documentToReactComponents(
                                    JSON.parse(refPlantilla[0].descripcion.raw)
                                )
                            }
                        </p>
                        {refPlantilla[0].tituloBoton && (
                            <>
                                <div className="banner-text__buttons">
                                    <div className="banner-text__buttons__item">
                                        <Button
                                            type="link"
                                            text={splitButtonText(
                                                refPlantilla[0].tituloBoton,
                                                0
                                            )}
                                            classname={`a-button__magenta gtmHomeClicBannerButtonPersonas`}
                                            href={splitButtonText(
                                                refPlantilla[0].redireccionBoton,
                                                0
                                            )}
                                            isDisabled={false}
                                        />
                                        {refPlantilla[0].tituloDescripcion && (
                                            <span>
                                                {splitButtonText(
                                                    refPlantilla[0].tituloDescripcion,
                                                    0
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    <div className="banner-text__buttons__item">
                                        <Button
                                            type="link"
                                            text={splitButtonText(
                                                refPlantilla[0].tituloBoton,
                                                1
                                            )}
                                            classname={`a-button__magenta gtmHomeClicBannerButtonEmpresas`}
                                            href={splitButtonText(
                                                refPlantilla[0].redireccionBoton,
                                                1
                                            )}
                                            isDisabled={false}
                                        />
                                        {refPlantilla[0].tituloDescripcion && (
                                            <span>
                                                {splitButtonText(
                                                    refPlantilla[0].tituloDescripcion,
                                                    1
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="banner-text__dots" ref={dotContainerRef}></div>
                    </div>
                </div>
                <Slider {...HomeSliderSettings} ref={sliderRef}>
                    {refPlantilla.map((item, i) => (
                        <div key={i}>
                            <img
                                src={isMobile
                                    ? `${item.imagenMobile?.file.url}`
                                    : `${item.imagenPrincipal[0].file.url}`}
                                alt={props.tituloGeneral || "Comfama imagen banner"}
                                loading={i === 0 ? "eager" : "lazy"}
                            />
                        </div>
                    ))}
                </Slider>
            </div>
        </div>
    );
};

export default MainBanner;
